import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardTitle, Button, Table, Badge, Alert, Collapse } from 'reactstrap';
import axios from 'axios';
import ImportTemplate from './ImportTemplate';

const TemplateList = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedTemplateId, setExpandedTemplateId] = useState(null);
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      const response = await axios.get(`${baseURL}/ssp-studio/templates`, {
        withCredentials: true
      });
      setTemplates(response.data.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err.response?.data?.message || err.message);
      setLoading(false);
    }
  };

  const publishTemplate = async (templateId) => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      await axios.post(
        `${baseURL}/ssp-studio/templates/${templateId}/publish`,
        {},
        { withCredentials: true }
      );
      setSuccess('Template published successfully! You can now use it to create SSPs.');
      setTimeout(() => setSuccess(null), 5000);
      fetchTemplates();
    } catch (err) {
      console.error('Error publishing template:', err);
      setError(err.response?.data?.message || err.message);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      await axios.delete(`${baseURL}/ssp-studio/templates/${templateId}`, {
        withCredentials: true
      });
      setSuccess('Template deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
      fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(err.response?.data?.message || err.message);
    }
  };

  const seedDefaultTemplate = async () => {
    try {
      setLoading(true);
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      await axios.post(
        `${baseURL}/ssp-studio/templates/seed/default`,
        {},
        { withCredentials: true }
      );
      setSuccess('Default FedRAMP template created and published! You can now create SSPs.');
      setTimeout(() => setSuccess(null), 5000);
      await fetchTemplates();
    } catch (err) {
      console.error('Error seeding template:', err);
      setError(err.response?.data?.message || err.message);
      setLoading(false);
    }
  };

  const toggleExpand = (templateId) => {
    setExpandedTemplateId(expandedTemplateId === templateId ? null : templateId);
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'warning',
      published: 'success',
      archived: 'secondary'
    };
    return <Badge color={colors[status] || 'info'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Container fluid>
      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert color="success" toggle={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Row className="mb-3">
        <Col>
          <Alert color="info" className="mb-0">
            <i className="mdi mdi-information me-2"></i>
            <strong>Templates are required to create SSPs.</strong> Start by creating the default FedRAMP template below. Templates must be <strong>published</strong> (not draft) before they can be used to create SSPs.
          </Alert>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">SSP Templates</h4>
            <div>
              <Button color="info" className="me-2" onClick={seedDefaultTemplate}>
                <i className="mdi mdi-database-plus me-1"></i>
                {templates.length === 0 ? 'Create Default Template' : 'Re-create Default Template'}
              </Button>
              <Button
                color="primary"
                onClick={() => setShowImport(!showImport)}
              >
                <i className="mdi mdi-file-import me-1"></i>
                {showImport ? 'Hide Import' : 'Import DOCX Template'}
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {showImport && (
        <Row className="mb-4">
          <Col>
            <ImportTemplate
              onSuccess={() => {
                setShowImport(false);
                fetchTemplates();
              }}
            />
          </Col>
        </Row>
      )}

      {templates.length === 0 ? (
        <Card>
          <CardBody className="text-center py-5">
            <i className="mdi mdi-file-document-outline text-muted" style={{ fontSize: '48px' }}></i>
            <h5 className="mt-3">No Templates Found</h5>
            <p className="text-muted">Create the default FedRAMP template to get started with SSP creation</p>
            <Button color="primary" size="lg" onClick={seedDefaultTemplate}>
              <i className="mdi mdi-database-plus me-1"></i>
              Create Default FedRAMP Template
            </Button>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardBody>
            <Table hover responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Sections</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <React.Fragment key={template._id}>
                    <tr>
                      <td>
                        <strong>{template.name}</strong>
                      </td>
                      <td>{template.version}</td>
                      <td>{getStatusBadge(template.status)}</td>
                      <td>
                        <Badge color="light" className="text-dark">
                          {template.sections?.length || 0} sections
                        </Badge>
                      </td>
                      <td>{new Date(template.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            color="info"
                            outline
                            onClick={() => toggleExpand(template._id)}
                          >
                            <i className={`mdi mdi-${expandedTemplateId === template._id ? 'chevron-up' : 'chevron-down'}`}></i>
                            {expandedTemplateId === template._id ? 'Hide' : 'View'}
                          </Button>
                          {template.status === 'draft' && (
                            <Button
                              size="sm"
                              color="success"
                              outline
                              onClick={() => publishTemplate(template._id)}
                            >
                              <i className="mdi mdi-publish"></i> Publish
                            </Button>
                          )}
                          <Button
                            size="sm"
                            color="danger"
                            outline
                            onClick={() => deleteTemplate(template._id)}
                          >
                            <i className="mdi mdi-delete"></i> Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="6" className="p-0">
                        <Collapse isOpen={expandedTemplateId === template._id}>
                          <div className="p-3 bg-light">
                            <h6>Template Sections:</h6>
                            <Table size="sm" bordered>
                              <thead>
                                <tr>
                                  <th style={{width: '60%'}}>Section Title</th>
                                  <th>Level</th>
                                  <th>Order</th>
                                </tr>
                              </thead>
                              <tbody>
                                {template.sections && template.sections.length > 0 ? (
                                  template.sections.map((section, idx) => (
                                    <tr key={idx}>
                                      <td style={{ paddingLeft: `${(section.level - 1) * 20 + 10}px` }}>
                                        {section.title}
                                      </td>
                                      <td>{section.level}</td>
                                      <td>{section.order_index}</td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="3" className="text-center text-muted">No sections defined</td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      )}
    </Container>
  );
};

export default TemplateList;
